import type { init } from "@sentry/nextjs"
import { isError } from "es-toolkit"
import { BUILD_ID, BUILD_TIMESTAMP, ENVIRONMENT } from "@/constants/env"
import { attachEventId } from "../error"
import { shouldSendErrorToSentry } from "./utils"

type Options = Parameters<typeof init>[0]

const BUILD_DATE = new Date(BUILD_TIMESTAMP)
const MAX_BUILD_AGE_MS = 3 * 24 * 60 * 60 * 1000

export const SENTRY_CONFIG: Partial<Options> = {
  dsn: process.env.SENTRY_DSN,
  enabled: process.env.NODE_ENV === "production",
  environment: ENVIRONMENT,
  release: BUILD_ID,
  skipOpenTelemetrySetup: true,
  normalizeDepth: 5,
  sampleRate: 0.2,

  beforeSend(event, hint) {
    // Filter out external errors before sending to Sentry
    const threeDaysAgo = new Date(Date.now() - MAX_BUILD_AGE_MS)

    // Drop events if build is older than 3 days
    if (BUILD_DATE < threeDaysAgo) {
      return null
    }

    if (event?.exception && !shouldSendErrorToSentry(event)) {
      return null // Don't send to Sentry
    }

    if (event.event_id && isError(hint.originalException)) {
      attachEventId(hint.originalException, event.event_id)
    }

    return event
  },

  // Block specific error messages
  ignoreErrors: [
    //https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/16
    "Non-Error promise rejection captured",
    "Non-Error exception captured",
    "ResizeObserver loop limit exceeded",
    // Network errors - typically transient connectivity issues
    /Failed to fetch/i,
    /Failed to load chunk/i,
  ],

  // Block external domains
  denyUrls: [
    /google-analytics\.com/,
    /googletagmanager\.com/,
    /facebook\.com/,
    /doubleclick\.net/,
    /googlesyndication\.com/,
    /chrome-extension:/,
    /moz-extension:/,
    /safari-extension:/,
  ],
}
